<template>
    <div>
        <AddPaymentMethodSidePanel
            v-if="showAddPaymentMethodSidePanel"
            key="editSubscriptionSidePanel"
            :disable-transition="true"
            :show-full-height="false"
            @close="showAddPaymentMethodSidePanel = false; disableTransition = true"
        />
        <CancelSubscriptionSidePanel
            v-if="showCancelSubscriptionSidePanel"
            :isLoading="isLoading"
            @selectionsUpdated="updateCancelSurveySelections"
            @close="showCancelSubscriptionSidePanel = false"
            @submitCancel="cancelSubscription"
        />
        <Portal v-if="!showAddPaymentMethodSidePanel" to="sidepanel">
            <SidePanel
                key="editSubscriptionSidePanel"
                class="edit-subscription"
                :class="{'edit-subscription--in-mobile-app': isInsideMobileInAppBrowser}"
                name="Edit Subscription"
                :disable-transition="disableTransition"
                width="medium"
                :show-cancel-button="!isInsideMobileInAppBrowser"
                :is-dark-mode="isDarkMode"
                @close="emitClose"
            >
                <div v-dark class="edit-subscription__label">
                    Subscription
                </div>
                <div v-dark class="edit-subscription__card">
                    <BundleIcon
                        v-if="bundle"
                        :bundle-id="bundle.objectId"
                        class="edit-subscription__card-bundle-icon"
                    />
                    <div v-dark class="edit-subscription__card-title">
                        {{ bundle && bundle.name }} Premium
                    </div>
                    <div v-dark class="edit-subscription__card-info">
                        Premium access to {{ (bundleUniqueExams && bundleUniqueExams.length) || 0 }}
                        {{ (bundleUniqueExams && bundleUniqueExams.length > 1) ? 'exams' : 'exam' }}, full question
                        banks, all quiz modes, and the
                        <PocketLink
                            href="https://help.pocketprep.com/en/articles/3697209-what-is-the-pass-guarantee"
                            target="_blank"
                            :is-dark-mode="isDarkMode"
                        >
                            pass guarantee.
                        </PocketLink>
                    </div>
                    <div
                        v-if="hasExpired || (subscription && !subscription.willAutoRenew)"
                        v-dark
                        class="edit-subscription__card-expired"
                    >
                        {{ hasExpired ? 'Expired' : 'Expires' }} {{ expirationDate }}
                    </div>
                    <div
                        v-else-if="subscription"
                        v-dark
                        class="edit-subscription__card-renews"
                    >
                        <template v-if="stripeSubscription && stripeSubCost">
                            {{ stripeSubCost }}
                            <span>•</span>
                        </template>
                        Renews {{ expirationDate }}
                    </div>
                    <template v-if="receipt && (receipt.source === 'Apple' || receipt.source === 'Google')">
                        <PocketLink
                            href="https://help.pocketprep.com/en/articles/3697799-how-does-the-subscription-work"
                            target="_blank"
                            class="edit-subscription__card-link"
                            :is-dark-mode="isDarkMode"
                        >
                            Manage Subscription on the {{ receipt.source === 'Apple' ? 'App' : 'Play' }} Store
                            <Icon type="externalLink" />
                        </PocketLink>
                        <div class="edit-subscription__card-helper">
                            Your subscription is managed through your
                            {{ receipt.source === 'Apple' ? 'Apple App' : 'Google Play' }} Store account. Questions on
                            editing your subscription? Our <PocketLink
                                href="https://help.pocketprep.com/en/articles/3697799-how-does-the-subscription-work"
                                target="_blank"
                                :is-dark-mode="isDarkMode"
                            >
                                help center
                            </PocketLink> has answers.
                        </div>
                    </template>
                </div>
                <template v-if="receipt.source === 'Stripe' || hasExpired">
                    <PocketSelect
                        v-model="plan"
                        label="Plan"
                        class="edit-subscription__plan"
                        :data="priceOptions"
                        :is-dark-mode="isDarkMode"
                    />
                    <PocketSelect
                        v-if="plan && plan.value !== 'basic'"
                        v-model="paymentMethod"
                        label="Payment Method"
                        class="edit-subscription__payment-method"
                        :data="paymentMethodOptions"
                        :is-dark-mode="isDarkMode"
                        @linkClick="addPaymentMethod"
                    />
                    <Errors
                        v-if="pmErrors.length"
                        class="edit-subscription__pm-errors"
                        :errors="pmErrors"
                        :is-dark-mode="isDarkMode"
                    />
                </template>
                <Errors
                    v-if="apiError"
                    class="edit-subscription__api-error"
                    :errors="[apiError]"
                    :is-dark-mode="isDarkMode"
                />
                <div v-dark class="edit-subscription__changes">
                    Changes made to your subscription will take effect immediately.
                    If you choose to update your subscription to a longer billing cycle,
                    changes in cost will be prorated.
                    If you cancel your subscription,
                    you will still have access to premium features until your expiration date.
                </div>
                <template #details>
                    <PocketButton
                        v-if="willAutoRenew"
                        :disabled="isLoading"
                        type="secondary"
                        class="edit-subscription__cancel"
                        :is-dark-mode="isDarkMode"
                        @click="showCancelSubscriptionSidePanel = true"
                    >
                        Cancel Subscription
                    </PocketButton>
                </template>
                <template #action>
                    <PocketButton
                        :is-dark-mode="isDarkMode"
                        :disabled="!areSettingsChanged || (!!plan && plan.value !== 'basic' && !paymentMethod)"
                        :is-loading="isLoading"
                        @click="saveChanges"
                    >
                        Save Changes
                    </PocketButton>
                </template>
            </SidePanel>
        </Portal>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { bundleModule } from '@/store/bundle/module'
import { stripeModule } from '@/store/stripe/module'
import { subscriptionModule } from '@/store/subscription/module'
import { userModule } from '@/store/user/module'
import { userExamMetadataModule } from '@/store/userExamMetadata/module'
import { months } from '@/utils'
import type { Study } from '@pocketprep/types'
import UIKit from '@pocketprep/ui-kit'
import AddPaymentMethodSidePanel from '@/components/Settings/AddPaymentMethodSidePanel.vue'
import CancelSubscriptionSidePanel from '@/components/Settings/CancelSubscriptionSidePanel.vue'
import { toastModule } from '@/store/toast/module'
import { analyticsModule } from '@/store/analytics/module'
import { mobileModule } from '@/store/mobile/module'
import { examMetadataModule } from '@/store/examMetadata/module'

const {
    SidePanel,
    Link: PocketLink,
    BundleIcon,
    Icon,
    Button: PocketButton,
    Select: PocketSelect,
    Errors,
} = UIKit

const props = defineProps<{
    subscription: ReturnType<Study.Class.SubscriptionBundle['toJSON']>
}>()

const emit = defineEmits<{
    close: []
}>()

const isLoading = ref(true)
const plan = ref<null | { label: string; value: string }>(null)
const originalPlan = ref<null | { label: string; value: string }>(null)
const paymentMethod = ref<null | { label: string; value: string }>(null)
const originalPaymentMethod = ref<null | { label: string; value: string }>(null)
const showAddPaymentMethodSidePanel = ref(false)
const showCancelSubscriptionSidePanel = ref(false)
const disableTransition = ref(false)
const cancellationSurveySelections = ref<{ examResult?: string; cancelReason?: string } | null>(null)
const cancellationFeedback = ref<string | null>(null)
const apiError = ref<string | null>(null)

const isDarkMode = computed(() => {
    return userModule.state.settings.isDarkMode
})

const bundle = computed(() => {
    return props.subscription
        && bundleModule.getters.getBundles().find(b => b.objectId === props.subscription.bundle.objectId)
})

const receipt = computed(() => {
    return props.subscription?.receipt as Study.Class.ReceiptJSON
})

const hasExpired = computed(() => {
    return new Date(props.subscription?.expiresAt.iso) < new Date()
})

const expirationDate = computed(() => {
    const date = new Date(props.subscription?.expiresAt.iso)
    return `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`
})

const willAutoRenew = computed(() => {
    return props.subscription.willAutoRenew
})

const stripeSubscription = computed(() => {
    const stripeSubscriptions = stripeModule.getters.getStripeSubscriptions()
    return stripeSubscriptions.find(s => s.id === props.subscription?.guid)
})

const stripeSubCost = computed(() => {
    const price = stripeSubscription.value?.items.data[0]?.price
    return price && stripeModule.getters.getPriceLabel({ p: price, isLongLabel: true })
})

const isOOAPSubscription = computed(() => {
    return stripeSubscription.value?.items.data[0]?.price.lookup_key?.includes('_mobile') ?? false
})

const priceOptions = computed(() => {
    return [
        ...(bundle.value && stripeModule.getters.getStripePriceOptions(
            bundle.value.objectId,
            undefined,
            isOOAPSubscription.value
        )) || [],
    ]
})

const paymentMethodOptions = computed(() => {
    return [
        ...stripeModule.getters.getStripePaymentMethods().map(pm => ({
            value: pm.id,
            label: `Card ending in ${pm.card?.last4} (Exp ${pm.card?.exp_month}/${
                String(pm.card?.exp_year || 0).slice(-2)})`,
        })),
        { value: 'add', label: '+ Add Payment Method', type: 'link' as const },
    ]
})

const pmErrors = computed(() => {
    const pm = stripeModule.getters.getStripePaymentMethods().find(spm => spm.id === paymentMethod.value?.value)
    const expiration = pm && new Date(`${pm?.card?.exp_month || 0 + 1}/01/${pm?.card?.exp_year}`)
    if (expiration && new Date().getTime() > expiration.getTime()) {
        return [ 'Card expired.' ]
    }

    return []
})

const areSettingsChanged = computed(() => {
    if (plan.value?.value !== originalPlan.value?.value
        || paymentMethod.value?.value !== originalPaymentMethod.value?.value
    ) {
        return true
    }

    return false
})

const userExamMetadata = computed(() => {
    return userExamMetadataModule.getters.getUserExamMetadata()
})

const bundleUniqueExams = computed(() => {
    return bundle.value && bundleModule.getters.getBundleUniqueExams(bundle.value.objectId)
})

const examGuidsInBundle = computed(() => {
    return bundleUniqueExams.value?.map(exam => exam.examGuid)
})

const currentUserExamMetadata = computed(() => {
    return userExamMetadataModule.getters.getCurrentUserExamMetadata()
})

const mostLikelyExamUserIsCancelling = computed(() => {
    const allUemInBundle = userExamMetadata.value.filter(uem => examGuidsInBundle.value?.includes(uem.examGuid))
    // if there is only one user exam metadata in the bundle then we assume it's the correct one
    if (allUemInBundle.length === 1) {
        return allUemInBundle[0]
    } else if (allUemInBundle.length > 1) {
        // if multiple exams from bundle are added, we assume the current exam is the correct if it's in the bundle
        if (allUemInBundle.some(uemInBundle => uemInBundle.examGuid === currentUserExamMetadata.value?.examGuid)) {
            return currentUserExamMetadata.value
        } else {
            // if current exam is not in the bundle, we assume most recently updated UEM in the bundle is correct
            return allUemInBundle
                .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())[0]
        }
    } else {
        // user shouldn't be able to have a bundle subscription to an exam they don't have a UEM for,
        // but just in case this somehow happens we will fallback to current exam
        return currentUserExamMetadata.value
    }
})

const examMetadataById = computed(() => {
    return examMetadataModule.getters.getExamMetadataById()
})

const isInsideMobileInAppBrowser = computed(() => {
    return mobileModule.getters.getPlatform() === 'iOS' || mobileModule.getters.getPlatform() === 'Android'
})

onMounted(async () => {
    await Promise.all([
        userModule.actions.fetchUserData(),
        bundleModule.actions.fetchBundles(),
        stripeModule.actions.fetchStripeSubscriptions(),
        stripeModule.actions.fetchStripePrices(),
        stripeModule.actions.fetchPaymentMethods(),
    ])
    plan.value = willAutoRenew.value
        ? priceOptions.value?.find(p => p.value === stripeSubscription.value?.items.data[0]?.price.id) || null
        : { value: 'basic', label: 'Free Prep' }
    originalPlan.value = plan.value
    paymentMethod.value = paymentMethodOptions.value.find(
        pm => pm.value === stripeSubscription.value?.default_payment_method
    ) || null
    originalPaymentMethod.value = paymentMethod.value

    isLoading.value = false
})

const addPaymentMethod = () => {
    showAddPaymentMethodSidePanel.value = true
}

const updateCancelSurveySelections = (selections: { [sectionId: string]: string | null }) => {
    cancellationSurveySelections.value = selections
}

const cancelSubscription = async (feedback?: string | null) => {
    if (feedback) {
        cancellationFeedback.value = feedback
    }
    plan.value = { value: 'basic', label: 'Free Prep' }
    await saveChanges()
}

const saveChanges = async () => {
    if (!plan.value || (plan.value.value !== 'basic' && !paymentMethod.value)) {
        throw new Error('No Payment Method or Plan selected.')
    }

    if (isLoading.value) {
        return
    }

    isLoading.value = true
    apiError.value = null

    try {
        if (receipt.value.source === 'Stripe') {
            await stripeModule.actions.updateStripeSubscription({
                paymentMethodId: paymentMethod.value?.value,
                subscriptionId: props.subscription.objectId,
                planId: plan.value.value,
                planChanged: plan.value?.value !== originalPlan.value?.value,
            })
        } else if (paymentMethod.value) {
            await subscriptionModule.actions.subscribeUser({
                paymentMethodId: paymentMethod.value.value,
                plan: plan.value.value,
            })
        }
    } catch (e) {
        apiError.value = e instanceof Error
            ? e.message
            : (e as { errorMessage?: string })?.errorMessage
                || 'Something went wrong. Please try again or contact support.'
        isLoading.value = false
        return
    }

    originalPlan.value = plan.value
    originalPaymentMethod.value = paymentMethod.value

    isLoading.value = false
    if (plan.value.value === 'basic') {
        const examMetadataId = mostLikelyExamUserIsCancelling.value?.examMetadata?.objectId
        const hasPassedExam = cancellationSurveySelections.value?.examResult === 'passed'
            ? true
            : cancellationSurveySelections.value?.examResult === 'failed'
                ? false
                : null
        if (hasPassedExam !== null && examMetadataId) {
            userExamMetadataModule.actions.submitExamResult({
                examMetadataId,
                passed: hasPassedExam,
            })
        }
        toastModule.actions.displayToast({
            title: 'Your Premium subscription was cancelled',
        })
        analyticsModule.actions.amplitudeTrack('Subscription Cancelled', {
            bundleId: bundle.value?.objectId || '',
            examResult: cancellationSurveySelections.value?.examResult || 'not_specified',
            reason: cancellationSurveySelections.value?.cancelReason || 'not_specified',
            feedback: cancellationFeedback.value || '',
            exam: examMetadataId ? examMetadataById.value[examMetadataId].nativeAppName : '',
        })
    } else {
        const cadence = plan.value.label.split(' - ')[0]
        toastModule.actions.displayToast({
            title: `Premium ${cadence} subscription activated!`,
        })
    }
    emitClose()
}

const emitClose = () => {
    emit('close')
}
</script>

<style lang="scss" scoped>
.edit-subscription {
    &__label {
        font-size: 13px;
        line-height: 14px;
        margin-bottom: 6px;
        color: $slate-01;

        &--dark {
            color: $fog;
        }
    }

    &__card {
        position: relative;
        background: $white;
        border-radius: 5px;
        box-shadow: 0 1px 6px 0 rgba($gray-divider, 0.6);
        padding: 12px;
        margin-left: -12px;
        margin-right: -12px;
        margin-bottom: 28px;

        &--dark {
            background: $moonlit-ocean;
            box-shadow: 0 1px 6px 0 rgba($charcoal, 0.3);
        }
    }

    &__card-bundle-icon {
        width: 52px;
        height: 52px;
        position: absolute;
        top: 1px;
        right: 3.5px;
    }

    &__card-title {
        font-size: 15px;
        line-height: 18px;
        font-weight: 600;
        margin-bottom: 4px;
        max-width: 310px;
    }

    &__card-info {
        font-size: 14px;
        line-height: 18px;
        color: $slate-03;
        max-width: 310px;
        margin-bottom: 8px;

        &--dark {
            color: $fog;
        }

        a {
            font-weight: 500;
        }
    }

    &__card-expired {
        font-size: 13px;
        line-height: 18px;
        color: $red-pegasus;

        &--dark {
            color: $rosa;
        }
    }

    &__card-renews {
        font-size: 13px;
        line-height: 18px;
        color: $slate-01;

        &--dark {
            color: $fog;
        }

        span {
            margin: 0 5px;
        }
    }

    &__card-link {
        margin-top: 13px;
        margin-bottom: 12px;
        display: block;

        svg {
            margin-left: 5px;
        }
    }

    &__card-helper {
        font-size: 13px;
        line-height: 18px;
        color: $slate-01;

        &--dark {
            color: $fog;
        }
    }

    &__plan {
        margin-bottom: 23px;
        margin-left: -12px;
        margin-right: -12px;
    }

    &__payment-method {
        margin-bottom: 14px;
        margin-left: -12px;
        margin-right: -12px;
    }

    &__pm-errors {
        margin-left: -12px;
        margin-right: -12px;
    }

    &__api-error {
        margin-left: -12px;
        margin-right: -12px;
        margin-top: 16px;
    }

    &__changes {
        font-size: 14px;
        line-height: 18px;
        color: $slate-03;
        margin-top: 32px;

        &--dark {
            color: $pewter;
        }
    }

    &__cancel {
        margin-left: -20px;
    }

    &--in-mobile-app    {

        :deep(.uikit-side-panel__head) {
            display: none;
        }

        :deep(.uikit-side-panel__foot-left) {
            margin-left: 11px;
        }
    }
}
</style>
